/**
 * Keeps a Shopify product form synchronized with selected option values and
 * submits additions to the cart over Ajax.
 *
 * DOM contract: the custom element contains a `<form>`, inputs with
 * `[data-option-position]`, a hidden `[name="id"]` variant input, price
 * targets, an add button `[data-add-to-cart]`, and an optional
 * `[data-add-to-cart-error]` message target.
 * Events listened for: `change` on option inputs, `submit` on the form,
 * `vellum:model-selected` (dispatched by assets/model-selector.js).
 * Events emitted: `vellum:model-selected` when the shopper picks a model
 * pill directly on this form, so other model-aware surfaces (the model
 * bar, other open tabs via storage) can react. `vellum:variant-media` when
 * the selected variant has a featured image, so assets/product-gallery.js
 * can jump the media gallery to match.
 * On success, the response's Section Rendering payload (requested via a
 * `sections` field alongside the normal add-to-cart body) is handed to
 * `window.vellumCart.applySections(..., { forceDrawerOpen: true })`
 * (assets/cart.js) so the header count, drawer contents, and cart page (if
 * present) all re-render from one response and the drawer opens - no
 * full-page reload.
 * Accessibility: unavailable state is reflected in visible button text and
 * native disabled state; submission errors are announced via
 * `[data-add-to-cart-error]`. During the Ajax request the add button gets
 * both `disabled` (prevents double submit) and a `data-loading` attribute
 * (shared `.button` component CSS uses this to show a spinner instead of
 * the dashed "unavailable" look that plain `:disabled` alone would render).
 *
 * Model gating: if a `[data-model-option]` fieldset is present (an option
 * whose name contains "model" or "iphone"), its radio inputs are marked
 * `[data-model-value]` and are never server-checked. A remembered model is
 * never auto-applied: on connect, if `vellum:selectedModel` is set in
 * localStorage, a `[data-model-confirm]` panel asks the shopper to confirm
 * it ("Yes, that's right" / "Choose a different model") before its radio is
 * checked. Add to Bag stays disabled with a `[data-model-notice]` reason
 * until either the confirmation is accepted or a pill is picked directly.
 */
class VellumProductForm extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[name="id"]');
    this.priceTarget = this.querySelector('[data-product-price]');
    this.compareTarget = this.querySelector('[data-product-compare-price]');
    this.unitTarget = this.querySelector('[data-product-unit-price]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.errorTarget = this.querySelector('[data-add-to-cart-error]');
    this.soldOutPanel = this.querySelector('[data-sold-out-panel]');
    this.modelFieldset = this.querySelector('[data-model-option]');
    this.modelNotice = this.querySelector('[data-model-notice]');
    this.modelConfirm = this.querySelector('[data-model-confirm]');
    this.modelConfirmText = this.querySelector('[data-model-confirm-text]');
    this.modelConfirmYes = this.querySelector('[data-model-confirm-yes]');
    this.modelConfirmChange = this.querySelector('[data-model-confirm-change]');
    this.stickyBar = this.querySelector('[data-sticky-buy-bar]');
    this.stickyButton = this.querySelector('[data-sticky-add-to-cart]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this.addText = this.dataset.addText || '';
    this.soldOutText = this.dataset.soldOutText || '';
    this.unavailableText = this.dataset.unavailableText || '';
    this.addingText = this.dataset.addingText || this.addText;
    this.errorText = this.dataset.errorText || '';
    this.modelRequiredText = this.dataset.modelRequiredText || '';
    this.modelUnavailableTemplate = this.dataset.modelUnavailableTemplate || '';
    this.modelConfirmTemplate = this.dataset.modelConfirmTemplate || '';
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.addEventListener('change', this.onChange.bind(this));
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    window.addEventListener('vellum:model-selected', this.onModelSelectedElsewhere.bind(this));
    this.modelConfirmYes?.addEventListener('click', this.onConfirmModel.bind(this));
    this.modelConfirmChange?.addEventListener('click', this.onDeclineStoredModel.bind(this));
    this.querySelector('[data-see-other-finishes]')?.addEventListener('click', this.onSeeOtherFinishes.bind(this));
    this.stickyButton?.addEventListener('click', () => this.form?.requestSubmit());
    this.initStickyBar();
    this.appliedVariantFromUrl = this.applyVariantFromUrl();
    this.applyStoredModel();
    this.prefillFromEditPersonalisationLink();
  }

  /**
   * Shows the sticky buy bar once the real Add to Bag button scrolls out of
   * view below the fold, so it's never visible at the same time as (or
   * before) the button it duplicates.
   */
  initStickyBar() {
    if (!this.stickyBar || !this.addButton || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(([entry]) => {
      this.stickyBar.hidden = entry.isIntersecting;
    });
    observer.observe(this.addButton);
  }

  onSeeOtherFinishes() {
    const finishFieldset = Array.from(this.querySelectorAll('fieldset')).find(
      (fieldset) => !fieldset.hasAttribute('data-model-option')
    );
    finishFieldset?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    finishFieldset?.querySelector('input')?.focus({ preventScroll: true });
  }

  /**
   * The cart's "Edit personalisation" link (snippets/cart-line-item.liquid)
   * sends the shopper back here with ?edit_properties=1 and the line's
   * current `properties[Name]=value` pairs as query params, since Shopify
   * has no native way to reopen a cart line's custom fields for editing -
   * changing them means remove-and-re-add. This fills in any
   * `[name="properties[Name]"]` field already on the form so the shopper
   * doesn't have to retype it, and scrolls to the first one filled.
   */
  prefillFromEditPersonalisationLink() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit_properties') !== '1') return;

    const propertyFields = Array.from(this.querySelectorAll('[name^="properties["]'));
    let firstField = null;

    params.forEach((value, key) => {
      const field = propertyFields.find((candidate) => candidate.name === key);
      if (!field) return;
      field.value = value;
      firstField = firstField || field;
    });

    firstField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    firstField?.focus({ preventScroll: true });
  }

  onChange(event) {
    if (!event.target.matches('[data-option-position]')) return;

    if (event.target.matches('[data-model-value]')) {
      window.localStorage.setItem('vellum:selectedModel', event.target.value);
      this.setModelRequired(false);
      window.dispatchEvent(new CustomEvent('vellum:model-selected', { detail: { model: event.target.value } }));
    }

    const variant = this.findSelectedVariant();
    this.renderVariant(variant);
  }

  applyStoredModel() {
    if (!this.modelFieldset) return;
    if (this.appliedVariantFromUrl) {
      this.setModelRequired(false);
      return;
    }

    const stored = window.localStorage.getItem('vellum:selectedModel');

    if (stored && this.modelConfirm) {
      this.showModelConfirm(stored);
    } else {
      this.setModelRequired(true);
    }
  }

  applyVariantFromUrl() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (!variantId) return false;

    const variant = this.variants.find((candidate) => String(candidate.id) === String(variantId));
    if (!variant) return false;

    variant.options.forEach((optionValue, index) => {
      this.selectOptionValue(index + 1, optionValue);
    });
    this.renderVariant(variant, { updateUrl: false });
    return true;
  }

  selectOptionValue(position, value) {
    const inputs = Array.from(this.querySelectorAll(`[data-option-position="${position}"]`));
    const input = inputs.find((candidate) => this.optionInputHasValue(candidate, value));
    if (!input) return;

    if (input.tagName === 'SELECT') {
      const matchedOption = Array.from(input.options).find((option) => this.optionValuesMatch(option.value, value));
      input.value = matchedOption?.value || value;
    } else {
      input.checked = true;
    }
  }

  showModelConfirm(model) {
    if (this.modelConfirmText) {
      this.modelConfirmText.textContent = this.modelConfirmTemplate.replace('[model]', model);
    }
    this.modelConfirm.hidden = false;
    this.modelConfirm.dataset.model = model;
    this.setModelRequired(true);
  }

  onConfirmModel() {
    const model = this.modelConfirm?.dataset.model;
    if (!model) return;
    // Dispatching (rather than calling selectModelValue directly) routes
    // through the same vellum:model-selected event every other model-aware
    // surface listens for, so this.onModelSelectedElsewhere below applies it
    // and hides the confirm panel in one path instead of two.
    window.dispatchEvent(new CustomEvent('vellum:model-selected', { detail: { model } }));
  }

  onDeclineStoredModel() {
    if (this.modelConfirm) this.modelConfirm.hidden = true;
    this.setModelRequired(true);
  }

  onModelSelectedElsewhere(event) {
    if (!this.modelFieldset || !event.detail?.model) return;
    if (this.modelConfirm) this.modelConfirm.hidden = true;
    this.selectModelValue(event.detail.model);
  }

  selectModelValue(model) {
    const input = Array.from(this.modelFieldset.querySelectorAll('[data-model-value]')).find(
      (candidate) => this.optionInputHasValue(candidate, model)
    );

    if (input) {
      if (input.tagName === 'SELECT') {
        input.value = Array.from(input.options).find(
          (option) => this.optionValuesMatch(option.value, model)
        )?.value || input.value;
      } else {
        input.checked = true;
      }
      this.setModelRequired(false);
      this.renderVariant(this.findSelectedVariant());
    } else {
      this.setModelRequired(true, model);
    }
  }

  setModelRequired(isRequired, unmatchedModel) {
    if (this.modelNotice) {
      this.modelNotice.hidden = !isRequired;
      this.modelNotice.textContent = unmatchedModel
        ? this.modelUnavailableTemplate.replace('[model]', unmatchedModel)
        : this.modelRequiredText;
    }

    if (isRequired && this.addButton) {
      this.addButton.disabled = true;
      this.addButton.textContent = this.addText;
    }
  }

  async onSubmit(event) {
    if (!this.cartAddUrl) return;
    event.preventDefault();

    if (this.errorTarget) {
      this.errorTarget.hidden = true;
      this.errorTarget.textContent = '';
    }

    const submitText = this.addButton?.textContent;
    if (this.addButton) {
      this.addButton.disabled = true;
      this.addButton.setAttribute('data-loading', '');
      this.addButton.textContent = this.addingText;
    }

    try {
      const formData = new FormData(this.form);
      const sectionIds = Array.from(document.querySelectorAll('[data-section-id]'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || this.errorText);

      if (document.body.dataset.cartType === 'page') {
        window.location.href = `${window.Shopify.routes.root}cart`;
        return;
      }

      if (window.vellumCart && data.sections) {
        window.vellumCart.applySections(data.sections, { forceDrawerOpen: true });
        window.dispatchEvent(new CustomEvent('vellum:cart-updated'));
      } else {
        window.sessionStorage.setItem('vellum:openCartDrawer', '1');
        window.location.reload();
      }

      if (this.addButton) {
        this.addButton.disabled = false;
        this.addButton.removeAttribute('data-loading');
        this.addButton.textContent = submitText;
      }
    } catch (error) {
      if (this.errorTarget) {
        this.errorTarget.hidden = false;
        this.errorTarget.textContent = error.message || this.errorText;
      }
      if (this.addButton) {
        this.addButton.disabled = false;
        this.addButton.removeAttribute('data-loading');
        this.addButton.textContent = submitText;
      }
    }
  }

  findSelectedVariant() {
    const selectedOptions = [];
    this.querySelectorAll('[data-option-position]:checked, select[data-option-position], input[type="hidden"][data-option-position]').forEach((input) => {
      selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
    });

    return this.variants.find((variant) =>
      variant.options.every((option, index) => this.optionValuesMatch(option, selectedOptions[index]))
    );
  }

  optionInputHasValue(input, value) {
    if (input.tagName === 'SELECT') {
      return Array.from(input.options).some((option) => this.optionValuesMatch(option.value, value));
    }

    return this.optionValuesMatch(input.value, value);
  }

  optionValuesMatch(firstValue, secondValue) {
    return String(firstValue || '').trim().toLowerCase() === String(secondValue || '').trim().toLowerCase();
  }

  renderVariant(variant, options = {}) {
    const shouldUpdateUrl = options.updateUrl !== false;

    if (!variant) {
      this.variantInput.value = '';
      this.addButton.disabled = true;
      this.addButton.textContent = this.unavailableText;
      if (this.soldOutPanel) this.soldOutPanel.hidden = true;
      if (this.stickyButton) this.stickyButton.disabled = true;
      return;
    }

    this.variantInput.value = variant.id;
    if (shouldUpdateUrl) this.updateVariantUrl(variant.id);
    this.addButton.disabled = !variant.available;
    this.addButton.textContent = variant.available ? this.addText : this.soldOutText;
    if (this.soldOutPanel) this.soldOutPanel.hidden = variant.available;
    if (this.stickyButton) this.stickyButton.disabled = !variant.available;
    if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(variant.price);

    this.dispatchVariantMedia(variant);

    if (this.priceTarget) this.priceTarget.textContent = this.formatMoney(variant.price);
    if (this.compareTarget) {
      this.compareTarget.textContent = variant.compare_at_price && variant.compare_at_price > variant.price
        ? this.formatMoney(variant.compare_at_price)
        : '';
    }
    if (this.unitTarget) this.unitTarget.hidden = true;
  }

  dispatchVariantMedia(variant) {
    if (!variant?.featured_media && !variant?.featured_image) return;

    const dispatch = () => {
      window.dispatchEvent(new CustomEvent('vellum:variant-media', {
        detail: {
          mediaId: variant.featured_media?.id,
          imageId: variant.featured_image?.id,
          imageSrc: variant.featured_image?.src,
        },
      }));
    };

    if (customElements.get('vellum-product-gallery')) {
      dispatch();
      return;
    }

    customElements.whenDefined('vellum-product-gallery').then(() => {
      window.requestAnimationFrame(dispatch);
    });
  }

  updateVariantUrl(variantId) {
    if (!variantId || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variantId)) return;

    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  formatMoney(cents) {
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return this.moneyFormat.replace('{{amount}}', amount).replace('{{ amount }}', amount);
  }
}

if (!customElements.get('vellum-product-form')) {
  customElements.define('vellum-product-form', VellumProductForm);
}
